/**
 * Meetings API service - handles meeting and transcription API calls
 */

import { apiClient } from './client';

// Enums
export type MeetingStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
export type TranscriptStatus = 'pending' | 'uploading' | 'processing' | 'completed' | 'failed';
export type AttachmentStatus = 'uploading' | 'uploaded' | 'processing' | 'processed' | 'failed';
export type MeetingSource =
  | 'ZOOM'
  | 'TEAMS'
  | 'GOOGLE_MEET'
  | 'MANUAL_UPLOAD'
  | 'MACOS_CALENDAR'
  | 'PLAUD'
  | 'OTHER';

// Types
export interface Meeting {
  id: string;
  title: string;
  description?: string | undefined;
  organizationId: string;
  projectId?: string | undefined;
  projectName?: string | undefined;
  startTime?: string | undefined;
  endTime?: string | undefined;
  durationSeconds?: number | undefined;
  status: MeetingStatus;
  transcriptStatus: TranscriptStatus;
  source: MeetingSource;
  externalMeetingId?: string | undefined;
  externalMeetingUrl?: string | undefined;
  metadata?: Record<string, unknown> | undefined;
  isHidden: boolean;
  hasNotes: boolean;  // Whether meeting has AI-generated notes
  createdById?: string | undefined;
  createdAt: string;
  updatedAt: string;
  attendees?: MeetingAttendee[] | undefined;
}

export interface MeetingDetail extends Omit<Meeting, 'attendees'> {
  transcription?: Transcription | undefined;
  attachments: MeetingAttachment[];
  attendees: MeetingAttendee[];
}

export interface Transcription {
  id: string;
  meetingId: string;
  rawTranscript?: string | undefined;
  formattedTranscript?: string | undefined;
  transcriptLanguage?: string | undefined;
  durationSeconds?: number | undefined;
  confidenceScore?: number | undefined;
  provider?: string | undefined;
  processingStartedAt?: string | undefined;
  processingCompletedAt?: string | undefined;
  errorMessage?: string | undefined;
  summary?: string | undefined;
  actionItems?: ActionItem[] | undefined;
  keyTopics?: string[] | undefined;
  notes?: string | undefined;  // Rich markdown meeting notes/synthesis
  speakerMapping?: Record<string, string> | undefined;  // Maps speaker labels (SPEAKER_00) to attendee names
  // Progress tracking fields
  progressPercent?: number | undefined;  // 0-100
  currentPhase?: 'diarization' | 'transcription' | 'post_processing' | undefined;
  phaseDetails?: {
    message?: string;
    etaSeconds?: number;
    updatedAt?: string;
  } | undefined;
  heartbeatAt?: string | undefined;
  estimatedDurationSeconds?: number | undefined;
  // RAG integration fields
  ragDocumentId?: string | undefined;  // RAG document ID for indexed notes
  ragSyncedAt?: string | undefined;  // Timestamp of last RAG sync
  createdAt: string;
  updatedAt: string;
}

export interface TranscriptionProgress {
  progressPercent?: number | null;  // 0-100
  currentPhase?: 'diarization' | 'transcription' | 'post_processing' | null;
  phaseDetails?: {
    message?: string | undefined;
    etaSeconds?: number | undefined;
    updatedAt?: string | undefined;
  } | null;
  heartbeatAt?: string | null;
  estimatedDurationSeconds?: number | null;
  status: TranscriptStatus;
}

export interface TranscriptionSegment {
  id: string;
  speakerId?: string | undefined;
  speakerName?: string | undefined;
  speakerLabel?: string | undefined;
  startTimeSeconds: number;
  endTimeSeconds: number;
  text: string;
  confidence?: number | undefined;
  sentiment?: string | undefined;
}

export interface TranscriptionDetail extends Transcription {
  segments: TranscriptionSegment[];
}

export interface MeetingAttachment {
  id: string;
  meetingId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadStatus: AttachmentStatus;
  durationSeconds?: number | undefined;
  isProcessed: boolean;
  /** Audio imported from Plaud */
  fromPlaud: boolean;
  createdAt: string;
}

export interface MeetingAttendee {
  id: string;
  meetingId: string;
  userId?: string | undefined;
  contactId?: string | undefined;  // Link to OrganizationContact
  email?: string | undefined;
  name?: string | undefined;
  role: string;
  attended: boolean;
}

export interface ActionItem {
  description: string;
  assignee?: string | undefined;
  dueDate?: string | undefined;
  priority?: string | undefined;
}

export interface ExtractedTask {
  title: string;
  description?: string | undefined;
  assigneeHint?: string | undefined;
  dueDateHint?: string | undefined;
  priorityHint?: string | undefined;
  sourceSegmentId?: string | undefined;
}

export interface TaskExtractionResponse {
  meetingId: string;
  tasks: ExtractedTask[];
  summary?: string | undefined;
  actionItemsCount: number;
}

// Request/Response types
export interface MeetingListResponse {
  items: Meeting[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface CreateMeetingRequest {
  organizationId: string;
  title: string;
  description?: string | undefined;
  projectId?: string | undefined;
  startTime?: string | undefined;
  endTime?: string | undefined;
  source?: MeetingSource | undefined;
  externalMeetingId?: string | undefined;
  externalMeetingUrl?: string | undefined;
  metadata?: Record<string, unknown> | undefined;
}

export interface UpdateMeetingRequest {
  title?: string | undefined;
  description?: string | undefined;
  projectId?: string | undefined;
  startTime?: string | undefined;
  endTime?: string | undefined;
  status?: MeetingStatus | undefined;
  metadata?: Record<string, unknown> | undefined;
}

export type MeetingSortField = 'date' | 'title' | 'status' | 'created';
export type SortOrder = 'asc' | 'desc';

export interface MeetingFilterParams {
  organizationId: string;
  projectId?: string | undefined;
  status?: MeetingStatus | undefined;
  transcriptStatus?: TranscriptStatus | undefined;
  search?: string | undefined;
  isHidden?: boolean | undefined;
  sortBy?: MeetingSortField | undefined;
  sortOrder?: SortOrder | undefined;
  /** ISO date-time lower bound on start_time (falls back to created_at server-side) */
  startDate?: string | undefined;
  /** ISO date-time upper bound on start_time (falls back to created_at server-side) */
  endDate?: string | undefined;
}

export interface AttachmentUploadRequest {
  fileName: string;
  fileSize: number;
  mimeType: string;
}

export interface AttachmentUploadResponse {
  attachmentId: string;
  uploadUrl: string;
  uploadFields?: Record<string, string> | undefined;
  expiresAt: string;
}

export interface CreateAttendeeRequest {
  userId?: string | undefined;
  contactId?: string | undefined;  // Link to existing OrganizationContact
  email?: string | undefined;
  name?: string | undefined;
  role?: string | undefined;
}

/**
 * Meetings API methods
 */
export const meetingsApi = {
  // =====================
  // Meeting CRUD
  // =====================

  /**
   * List meetings with filtering
   */
  async list(
    filters: MeetingFilterParams,
    page = 1,
    pageSize = 50
  ): Promise<MeetingListResponse> {
    const params: Record<string, string | number | boolean> = {
      organization_id: filters.organizationId,
      page,
      page_size: pageSize,
    };
    if (filters.projectId) params.project_id = filters.projectId;
    if (filters.status) params.status = filters.status;
    if (filters.transcriptStatus) params.transcript_status = filters.transcriptStatus;
    if (filters.search) params.search = filters.search;
    if (filters.isHidden !== undefined) params.is_hidden = filters.isHidden;
    if (filters.sortBy) params.sort_by = filters.sortBy;
    if (filters.sortOrder) params.sort_order = filters.sortOrder;
    if (filters.startDate) params.start_date = filters.startDate;
    if (filters.endDate) params.end_date = filters.endDate;

    const response = await apiClient.get<any>('/meetings', { params });
    const data = response.data;

    return {
      items: (data.items ?? []).map(transformMeeting),
      total: data.total,
      page: data.page,
      pageSize: data.pageSize ?? data.page_size,
      hasMore: data.hasMore ?? data.has_more,
    };
  },

  /**
   * Get meeting by ID with full details
   */
  async get(meetingId: string): Promise<MeetingDetail> {
    // Add cache-busting timestamp to ensure fresh data during polling
    const response = await apiClient.get<unknown>(`/meetings/${meetingId}`, {
      params: { _t: Date.now() },
    });
    return transformMeetingDetail(response.data);
  },

  /**
   * Create a new meeting
   */
  async create(data: CreateMeetingRequest): Promise<Meeting> {
    const response = await apiClient.post<unknown>('/meetings', {
      organization_id: data.organizationId,
      title: data.title,
      description: data.description,
      project_id: data.projectId,
      start_time: data.startTime,
      end_time: data.endTime,
      source: data.source || 'MANUAL_UPLOAD',
      external_meeting_id: data.externalMeetingId,
      external_meeting_url: data.externalMeetingUrl,
      metadata: data.metadata,
    });
    return transformMeeting(response.data);
  },

  /**
   * Update a meeting
   */
  async update(meetingId: string, data: UpdateMeetingRequest): Promise<Meeting> {
    const payload: Record<string, unknown> = {};
    if (data.title !== undefined) payload.title = data.title;
    if (data.description !== undefined) payload.description = data.description;
    if (data.projectId !== undefined) payload.project_id = data.projectId;
    if (data.startTime !== undefined) payload.start_time = data.startTime;
    if (data.endTime !== undefined) payload.end_time = data.endTime;
    if (data.status !== undefined) payload.status = data.status;
    if (data.metadata !== undefined) payload.metadata = data.metadata;

    const response = await apiClient.patch<unknown>(`/meetings/${meetingId}`, payload);
    return transformMeeting(response.data);
  },

  /**
   * Delete a meeting
   */
  async delete(meetingId: string): Promise<void> {
    await apiClient.delete(`/meetings/${meetingId}`);
  },

  /**
   * Delete multiple meetings at once
   */
  async bulkDelete(meetingIds: string[]): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(
      '/meetings/bulk-delete',
      meetingIds
    );
    return response.data;
  },

  // =====================
  // Visibility (Hide/Unhide)
  // =====================

  /**
   * Toggle meeting visibility (hide/unhide)
   */
  async toggleHidden(meetingId: string, isHidden: boolean): Promise<Meeting> {
    const response = await apiClient.patch<unknown>(
      `/meetings/${meetingId}/hidden`,
      null,
      { params: { is_hidden: isHidden } }
    );
    return transformMeeting(response.data);
  },

  /**
   * Hide or unhide multiple meetings at once
   */
  async bulkHide(
    meetingIds: string[],
    isHidden: boolean,
    organizationId: string
  ): Promise<{ updatedCount: number }> {
    const response = await apiClient.post<{ updated_count: number }>(
      '/meetings/bulk-hide',
      { meeting_ids: meetingIds, is_hidden: isHidden },
      { params: { organization_id: organizationId } }
    );
    return { updatedCount: response.data.updated_count };
  },

  // =====================
  // Attachments
  // =====================

  /**
   * Initiate attachment upload - gets presigned URL
   */
  async initiateUpload(
    meetingId: string,
    data: AttachmentUploadRequest
  ): Promise<AttachmentUploadResponse> {
    const response = await apiClient.post<{
      attachment_id: string;
      upload_url: string;
      upload_fields?: Record<string, string>;
      expires_at: string;
    }>(`/meetings/${meetingId}/attachments/initiate`, {
      file_name: data.fileName,
      file_size: data.fileSize,
      mime_type: data.mimeType,
    });

    return {
      attachmentId: response.data.attachment_id,
      uploadUrl: response.data.upload_url,
      uploadFields: response.data.upload_fields,
      expiresAt: response.data.expires_at,
    };
  },

  /**
   * Complete attachment upload - triggers transcription
   */
  async completeUpload(
    meetingId: string,
    attachmentId: string
  ): Promise<MeetingAttachment> {
    const response = await apiClient.post<unknown>(
      `/meetings/${meetingId}/attachments/${attachmentId}/complete`
    );
    return transformAttachment(response.data);
  },

  /**
   * List attachments for a meeting
   */
  async listAttachments(meetingId: string): Promise<MeetingAttachment[]> {
    const response = await apiClient.get<unknown[]>(
      `/meetings/${meetingId}/attachments`
    );
    return response.data.map(transformAttachment);
  },

  // =====================
  // Transcription
  // =====================

  /**
   * Get transcription for a meeting
   */
  async getTranscription(meetingId: string): Promise<TranscriptionDetail> {
    const response = await apiClient.get<unknown>(
      `/meetings/${meetingId}/transcription`
    );
    return transformTranscriptionDetail(response.data);
  },

  /**
   * Retry a failed transcription
   */
  async retryTranscription(meetingId: string): Promise<void> {
    await apiClient.post(`/meetings/${meetingId}/transcription/retry`);
  },

  /**
   * Reset transcription to allow re-uploading a new audio file
   */
  async resetTranscription(meetingId: string): Promise<void> {
    await apiClient.post(`/meetings/${meetingId}/transcription/reset`);
  },

  /**
   * Move this meeting's Plaud recording to a meeting of its own
   * (it was matched to the wrong calendar meeting). Returns the new meeting.
   */
  async detachRecording(meetingId: string): Promise<Meeting> {
    const response = await apiClient.post<unknown>(`/meetings/${meetingId}/detach-recording`);
    return transformMeeting(response.data);
  },

  /**
   * Get real-time transcription progress
   */
  async getTranscriptionProgress(meetingId: string): Promise<TranscriptionProgress> {
    const response = await apiClient.get<{
      progress_percent?: number | null;
      current_phase?: 'diarization' | 'transcription' | 'post_processing' | null;
      phase_details?: {
        message?: string;
        eta_seconds?: number | null;
        updated_at?: string;
      } | null;
      heartbeat_at?: string | null;
      estimated_duration_seconds?: number | null;
      status: string;
    }>(`/meetings/${meetingId}/transcription/progress`);

    return {
      progressPercent: response.data.progress_percent ?? null,
      currentPhase: response.data.current_phase ?? null,
      phaseDetails: response.data.phase_details
        ? {
            message: response.data.phase_details.message ?? undefined,
            etaSeconds: response.data.phase_details.eta_seconds ?? undefined,
            updatedAt: response.data.phase_details.updated_at ?? undefined,
          }
        : null,
      heartbeatAt: response.data.heartbeat_at ?? null,
      estimatedDurationSeconds: response.data.estimated_duration_seconds ?? null,
      status: response.data.status as TranscriptStatus,
    };
  },

  // =====================
  // AI Features
  // =====================

  /**
   * Extract tasks from meeting transcription
   */
  async extractTasks(meetingId: string): Promise<TaskExtractionResponse> {
    const response = await apiClient.post<{
      meeting_id: string;
      tasks: Array<{
        title: string;
        description?: string;
        assignee_hint?: string;
        due_date_hint?: string;
        priority_hint?: string;
        source_segment_id?: string;
      }>;
      summary?: string;
      action_items_count: number;
    }>(`/meetings/${meetingId}/extract-tasks`);

    return {
      meetingId: response.data.meeting_id,
      tasks: response.data.tasks.map((t) => ({
        title: t.title,
        description: t.description,
        assigneeHint: t.assignee_hint,
        dueDateHint: t.due_date_hint,
        priorityHint: t.priority_hint,
        sourceSegmentId: t.source_segment_id,
      })),
      summary: response.data.summary,
      actionItemsCount: response.data.action_items_count,
    };
  },

  /**
   * Generate AI summary for meeting
   */
  async summarize(meetingId: string): Promise<Transcription> {
    const response = await apiClient.post<unknown>(
      `/meetings/${meetingId}/summarize`
    );
    return transformTranscription(response.data);
  },

  /**
   * Generate rich markdown meeting notes from transcription
   * @param meetingId - The meeting ID
   * @param language - Optional language code (en, fr, es, de) to override user's default locale
   */
  async generateNotes(meetingId: string, language?: string): Promise<Transcription> {
    // Use extended timeout for notes generation (10 minutes)
    // Long transcripts may take several minutes to process with AI
    const response = await apiClient.post<unknown>(
      `/meetings/${meetingId}/generate-notes`,
      language ? { language } : undefined,
      { timeout: 600000 }
    );
    return transformTranscription(response.data);
  },

  /**
   * Update meeting notes manually
   */
  async updateNotes(meetingId: string, notes: string): Promise<Transcription> {
    const response = await apiClient.patch<unknown>(
      `/meetings/${meetingId}/notes`,
      { notes }
    );
    return transformTranscription(response.data);
  },

  /**
   * Update speaker mapping for transcription
   */
  async updateSpeakerMapping(
    meetingId: string,
    speakerMapping: Record<string, string>
  ): Promise<Transcription> {
    const response = await apiClient.patch<unknown>(
      `/meetings/${meetingId}/speaker-mapping`,
      { speaker_mapping: speakerMapping }
    );
    return transformTranscription(response.data);
  },

  /**
   * Paste a transcript directly (without uploading an audio file)
   */
  async pasteTranscript(
    meetingId: string,
    transcript: string,
    language: string = 'en'
  ): Promise<Transcription> {
    const response = await apiClient.post<unknown>(
      `/meetings/${meetingId}/transcription/paste`,
      { transcript, language }
    );
    return transformTranscription(response.data);
  },

  // =====================
  // Attendees
  // =====================

  /**
   * Add attendees to a meeting
   */
  async addAttendees(
    meetingId: string,
    attendees: CreateAttendeeRequest[]
  ): Promise<MeetingAttendee[]> {
    const response = await apiClient.post<unknown[]>(
      `/meetings/${meetingId}/attendees`,
      attendees.map((a) => {
        const payload: Record<string, string> = {
          role: a.role || 'attendee',
        };
        // Only include properties that have values to avoid sending undefined/null
        if (a.userId) payload.user_id = a.userId;
        if (a.email) payload.email = a.email;
        if (a.name) payload.name = a.name;
        return payload;
      })
    );
    return response.data.map(transformAttendee);
  },

  /**
   * List attendees for a meeting
   */
  async listAttendees(meetingId: string): Promise<MeetingAttendee[]> {
    const response = await apiClient.get<unknown[]>(
      `/meetings/${meetingId}/attendees`
    );
    return response.data.map(transformAttendee);
  },

  /**
   * Remove an attendee from a meeting
   */
  async removeAttendee(meetingId: string, attendeeId: string): Promise<void> {
    await apiClient.delete(`/meetings/${meetingId}/attendees/${attendeeId}`);
  },
};

// Transform functions
// Note: Backend uses alias_generator=to_camel, so API responses are camelCase
function transformMeeting(data: unknown): Meeting {
  const d = data as Record<string, unknown>;
  return {
    id: d.id as string,
    title: d.title as string,
    description: d.description as string | undefined,
    organizationId: (d.organizationId ?? d.organization_id) as string,
    projectId: (d.projectId ?? d.project_id) as string | undefined,
    projectName: (d.projectName ?? d.project_name) as string | undefined,
    startTime: (d.startTime ?? d.start_time) as string | undefined,
    endTime: (d.endTime ?? d.end_time) as string | undefined,
    durationSeconds: (d.durationSeconds ?? d.duration_seconds) as number | undefined,
    status: d.status as MeetingStatus,
    transcriptStatus: (d.transcriptStatus ?? d.transcript_status) as TranscriptStatus,
    source: d.source as MeetingSource,
    externalMeetingId: (d.externalMeetingId ?? d.external_meeting_id) as string | undefined,
    externalMeetingUrl: (d.externalMeetingUrl ?? d.external_meeting_url) as string | undefined,
    metadata: d.metadata as Record<string, unknown> | undefined,
    isHidden: ((d.isHidden ?? d.is_hidden) as boolean) ?? false,
    hasNotes: ((d.hasNotes ?? d.has_notes) as boolean) ?? false,
    createdById: (d.createdById ?? d.created_by_id) as string | undefined,
    createdAt: (d.createdAt ?? d.created_at) as string,
    updatedAt: (d.updatedAt ?? d.updated_at) as string,
    attendees: d.attendees ? ((d.attendees as unknown[]) || []).map(transformAttendee) : undefined,
  };
}

function transformMeetingDetail(data: unknown): MeetingDetail {
  const d = data as Record<string, unknown>;
  const base = transformMeeting(data);
  const result: MeetingDetail = {
    ...base,
    attachments: ((d.attachments as unknown[]) || []).map(transformAttachment),
    attendees: ((d.attendees as unknown[]) || []).map(transformAttendee),
  };

  if (d.transcription) {
    result.transcription = transformTranscription(d.transcription);
  }

  return result;
}

function transformTranscription(data: unknown): Transcription {
  const d = data as Record<string, unknown>;
  return {
    id: d.id as string,
    meetingId: (d.meetingId ?? d.meeting_id) as string,
    rawTranscript: (d.rawTranscript ?? d.raw_transcript) as string | undefined,
    formattedTranscript: (d.formattedTranscript ?? d.formatted_transcript) as string | undefined,
    transcriptLanguage: (d.transcriptLanguage ?? d.transcript_language) as string | undefined,
    durationSeconds: (d.durationSeconds ?? d.duration_seconds) as number | undefined,
    confidenceScore: (d.confidenceScore ?? d.confidence_score) as number | undefined,
    provider: d.provider as string | undefined,
    processingStartedAt: (d.processingStartedAt ?? d.processing_started_at) as string | undefined,
    processingCompletedAt: (d.processingCompletedAt ?? d.processing_completed_at) as string | undefined,
    errorMessage: (d.errorMessage ?? d.error_message) as string | undefined,
    summary: d.summary as string | undefined,
    actionItems: (d.actionItems ?? d.action_items) as ActionItem[] | undefined,
    keyTopics: (d.keyTopics ?? d.key_topics) as string[] | undefined,
    notes: d.notes as string | undefined,
    speakerMapping: (d.speakerMapping ?? d.speaker_mapping) as Record<string, string> | undefined,
    // RAG integration fields
    ragDocumentId: (d.ragDocumentId ?? d.rag_document_id) as string | undefined,
    ragSyncedAt: (d.ragSyncedAt ?? d.rag_synced_at) as string | undefined,
    createdAt: (d.createdAt ?? d.created_at) as string,
    updatedAt: (d.updatedAt ?? d.updated_at) as string,
  };
}

function transformTranscriptionDetail(data: unknown): TranscriptionDetail {
  const d = data as Record<string, unknown>;
  const base = transformTranscription(data);
  return {
    ...base,
    segments: ((d.segments as unknown[]) || []).map(transformSegment),
  };
}

function transformSegment(data: unknown): TranscriptionSegment {
  const d = data as Record<string, unknown>;
  return {
    id: d.id as string,
    speakerId: (d.speakerId ?? d.speaker_id) as string | undefined,
    speakerName: (d.speakerName ?? d.speaker_name) as string | undefined,
    speakerLabel: (d.speakerLabel ?? d.speaker_label) as string | undefined,
    startTimeSeconds: (d.startTimeSeconds ?? d.start_time_seconds) as number,
    endTimeSeconds: (d.endTimeSeconds ?? d.end_time_seconds) as number,
    text: d.text as string,
    confidence: d.confidence as number | undefined,
    sentiment: d.sentiment as string | undefined,
  };
}

function transformAttachment(data: unknown): MeetingAttachment {
  const d = data as Record<string, unknown>;
  return {
    id: d.id as string,
    meetingId: (d.meetingId ?? d.meeting_id) as string,
    fileName: (d.fileName ?? d.file_name) as string,
    fileSize: (d.fileSize ?? d.file_size) as number,
    mimeType: (d.mimeType ?? d.mime_type) as string,
    uploadStatus: (d.uploadStatus ?? d.upload_status) as AttachmentStatus,
    durationSeconds: (d.durationSeconds ?? d.duration_seconds) as number | undefined,
    isProcessed: (d.isProcessed ?? d.is_processed) as boolean,
    fromPlaud: ((d.fromPlaud ?? d.from_plaud) as boolean | undefined) ?? false,
    createdAt: (d.createdAt ?? d.created_at) as string,
  };
}

function transformAttendee(data: unknown): MeetingAttendee {
  const d = data as Record<string, unknown>;
  return {
    id: d.id as string,
    meetingId: (d.meetingId ?? d.meeting_id) as string,
    userId: (d.userId ?? d.user_id) as string | undefined,
    email: d.email as string | undefined,
    name: d.name as string | undefined,
    role: d.role as string,
    attended: d.attended as boolean,
  };
}

export default meetingsApi;
